import React, { useEffect, useContext, useState, useRef } from 'react';
import FadeIn from 'react-fade-in';
import { GlobalHotKeys } from 'react-hotkeys';
import { useObserver } from 'mobx-react-lite';
import { Mention } from 'devextreme-react/html-editor';

import BodyEnd from '../../../../_shared/BodyEnd';
import QuickDrawerHeader from '../../../../_shared/QuickDrawerHeader';
import { quickDrawerFocus } from '../../../../_shared/QuickDrawer';
import RichTextEditor from '../../../../_shared/RichTextEditor';
import HtmlEditor from '../../../../_shared/HtmlEditor';

import ExamInputCreateStore from '../../../../../../stores/ExamInputCreateStore';
import QuickDrawerStore from '../../../../../../stores/QuickDrawerStore';
import CacheStore from '../../../../../../stores/CacheStore';

import * as ErrorMessages from '../../../../../../constants/errorMessages';
import * as fn from '../../../../../../utilities/_functions';
import * as oh from '../../../../../../utilities/operationHelper';
import * as tih from '../../../../../../utilities/templateInputHelper';

import './NewExamInput.scss';

function NewExamInput(props) {
    const isMounted = useRef(true);
    const focusTimer = useRef(null);
    const expressionRef = useRef(null);
    const examInput = useContext(ExamInputCreateStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const cache = useContext(CacheStore);
    const [metadataReady, setMetadataReady] = useState((props.extraProps && props.extraProps.metadataReady ? props.extraProps.metadataReady : false));
    const [displayPreview, setDisplayPreview] = useState(false);
    const [size, setSize] = useState('');
    const [align, setAlign] = useState('left');
    const [valign, setValign] = useState('top');
    const [options, setOptions] = useState([]);
    const [inline, setInline] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [imageWidth, setImageWidth] = useState(null);
    const [imageHeight, setImageHeight] = useState(null);
    const [defaultValue, setDefaultValue] = useState(null);
    const [dataType, setDataType] = useState(null);
    const [paddingType, setPaddingType] = useState(null);
    const [paddingCharacter, setPaddingCharacter] = useState(null);
    const [paddingLength, setPaddingLength] = useState(null);
    const [decimalPlaces, setDecimalPlaces] = useState(null);
    const [isDefaultNegative, setIsDefaultNegative] = useState(null);
    const [format, setFormat] = useState(null);
    const [formatSearch, setFormatSearch] = useState(null);
    const [formatReplace, setFormatReplace] = useState(null);
    const [validation, setValidation] = useState(null);
    const [step, setStep] = useState(null);
    const [fileText, setFileText] = useState(null);
    const [filePreview, setFilePreview] = useState(false);
    const [fileMultiple, setFileMultiple] = useState(false);
    const [historyTooltipEnabled, setHistoryTooltipEnabled] = useState(false);
    const [historyTooltipExpression, setHistoryTooltipExpression] = useState(null);

    useEffect(() => {
        focusTimer.current = setTimeout(() => {
            if (props.extraProps && props.extraProps.metadataReady === true) {
                setSize(examInput.getMetadata('size'));
                setAlign(examInput.getMetadata('align'));
                setValign(examInput.getMetadata('valign'));
                setOptions(examInput.getMetadata('options'));
                setInline(examInput.getMetadata('inline'));
                setImageUrl(examInput.getMetadata('imageUrl'));
                setImageWidth(examInput.getMetadata('imageWidth'));
                setImageHeight(examInput.getMetadata('imageHeight'));
                setDefaultValue(examInput.getMetadata('defaultValue'));
                setDataType(examInput.getMetadata('dataType'));
                setPaddingType(examInput.getMetadata('paddingType'));
                setPaddingCharacter(examInput.getMetadata('paddingCharacter'));
                setPaddingLength(examInput.getMetadata('paddingLength'));
                setDecimalPlaces(examInput.getMetadata('decimalPlaces'));
                setIsDefaultNegative(examInput.getMetadata('isDefaultNegative'));
                setFormat(examInput.getMetadata('format'));
                setFormatSearch(examInput.getMetadata('formatSearch'));
                setFormatReplace(examInput.getMetadata('formatReplace'));
                setValidation(examInput.getMetadata('validation'));
                setStep(examInput.getMetadata('step'));
                setFileText(examInput.getMetadata('fileText'));
                setFilePreview(examInput.getMetadata('filePreview'));
                setFileMultiple(examInput.getMetadata('fileMultiple'));
                setHistoryTooltipEnabled(examInput.getMetadata('historyTooltipEnabled'));
                setHistoryTooltipExpression(examInput.getMetadata('historyTooltipExpression'));
            }

            quickDrawerFocus(props.drawer);
        }, 100)

        return (() => {
            isMounted.current = false;
            examInput.clear();
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        })
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (examInput.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = event => {
        event.preventDefault();
        handlePopulateMetadata();
        examInput.save()
            .then(data => {
                if (isMounted.current) {
                    if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                        props.onSuccess(event, data);
                    }
                }
            })
    }

    const handleTypeChange = event => {
        examInput.data.type = event.target.value;

        if (!examInput.data.type) { setMetadataReady(false); return; }

        if (metadataReady) {
            examInput.data.metadata = null;
            setMetadataReady(false);
            focusTimer.current = setTimeout(() => {
                setMetadataReady(true);
                setFocusToName();
            }, 100);
        } else {
            setMetadataReady(true);
            setFocusToName();
        }

        examInput.hasUnsavedChanges = true;
    }

    const handleNameChange = event => {
        examInput.data.name = event.target.value;
        examInput.data.label = event.target.value;
        examInput.data.key = event.target.value.replace(/[^0-9a-zA-Z]/g, '');
        examInput.data.lookupKey = event.target.value.replace(/[^0-9a-zA-Z]/g, '');
        examInput.hasUnsavedChanges = true;
    }

    const handleLabelChange = event => {
        examInput.data.label = event.target.value;
        examInput.hasUnsavedChanges = true;
    }

    const handleKeyChange = event => {
        examInput.data.key = event.target.value.replace(/[^0-9a-zA-Z]/g, '');
        examInput.data.lookupKey = event.target.value.replace(/[^0-9a-zA-Z]/g, '');
        examInput.hasUnsavedChanges = true;
    }

    const handleDescriptionChange = content => {
        const html = content;
        examInput.data.descriptionHtml = ((html === '<p><br></p>') ? null : html);
        examInput.hasUnsavedChanges = true;
    }

    const handleSizeChange = event => {
        setSize(event.target.value);
        examInput.hasUnsavedChanges = true;
    }

    const handleAlignChange = value => {
        setAlign((value ? value : 'left'));
        examInput.hasUnsavedChanges = true;
    }

    const handleValignChange = value => {
        setValign((value ? value : 'top'));
        examInput.hasUnsavedChanges = true;
    }

    const handleInlineChange = event => {
        setInline((event.target.value === 'true'));
        examInput.hasUnsavedChanges = true;
    }

    const handlePreviewClick = event => {
        event.preventDefault();
        handlePopulateMetadata();
        setDisplayPreview(true);
    }

    const handleClosePreviewClick = event => {
        event.preventDefault();
        setDisplayPreview(false);
    }

    const handlePopulateMetadata = () => {
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_SIZE, 'size', size);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_ALIGN, 'align', align);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_VALIGN, 'valign', valign);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_OPTIONS, 'options', options);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_INLINE, 'inline', inline);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_IMAGE_URL, 'imageUrl', imageUrl);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_IMAGE_URL, 'imageWidth', imageWidth);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_IMAGE_URL, 'imageHeight', imageHeight);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DEFAULT_VALUE, 'defaultValue', defaultValue);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_TYPE, 'dataType', dataType);
        if (!!paddingType && !!paddingCharacter && !!paddingLength) {
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_TYPE, 'paddingType', paddingType);
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_CHARACTER, 'paddingCharacter', paddingCharacter);
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_LENGTH, 'paddingLength', paddingLength);
        }
        else {
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_TYPE, 'paddingType', null);
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_CHARACTER, 'paddingCharacter', null);
            populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_LENGTH, 'paddingLength', null);
        }
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_DEFAULT_NEGATIVE, 'isDefaultNegative', isDefaultNegative);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_DECIMAL_PLACES, 'decimalPlaces', decimalPlaces);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT, 'format', format);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT_SEARCH, 'formatSearch', formatSearch);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT_REPLACE, 'formatReplace', formatReplace);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_VALIDATION, 'validation', validation);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_DATA_STEP, 'step', step);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_FILE_TEXT, 'fileText', fileText);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_FILE_PREVIEW, 'filePreview', filePreview);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_FILE_MULTIPLE, 'fileMultiple', fileMultiple);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_HISTORY_TOOLTIP, 'historyTooltipEnabled', historyTooltipEnabled);
        populateMetadata(tih.TEMPLATE_INPUT_METADATA_HISTORY_TOOLTIP_EXPRESSION, 'historyTooltipExpression', historyTooltipExpression);
    }

    const handleOptionChange = (event, index) => {
        const tempOptions = [...options];
        tempOptions[index] = event.target.value;
        setOptions(tempOptions);
        examInput.hasUnsavedChanges = true;
    }

    const handleAddOption = event => {
        event.preventDefault();
        setOptions(o => [...options, '']);
        focusTimer.current = setTimeout(() => {
            const element = document.querySelector(`#new-exam-input-${examInput.data.key}-${(options.length)}`)
            if (element) { element.focus(); }
        }, 50);
        examInput.hasUnsavedChanges = true;
    }

    const handleRemoveOption = (option, index) => {
        setOptions(options.filter((o, oi) => oi !== index));
        examInput.hasUnsavedChanges = true;
    }

    const handleMoveOption = (from, to) => {
        const tempOptions = [...options];
        const element = tempOptions[from];

        tempOptions.splice(from, 1);
        tempOptions.splice(to, 0, element);

        setOptions(tempOptions);
        examInput.hasUnsavedChanges = true;
    }

    const handleImageAnnotationChange = event => {
        const file = event.target.files[0];
        const reader = new FileReader();

        reader.onload = handleFileReaderLoad;
        reader.readAsDataURL(file);
    }

    const handleFileReaderLoad = (event) => {
        const reader = event.target;
        const base64 = reader.result;
        const tempImage = new Image();

        tempImage.src = event.target.result;
        tempImage.onload = () => {
            setImageWidth(tempImage.width);
            setImageHeight(tempImage.height);
        }
        setImageUrl(base64);
        examInput.hasUnsavedChanges = true;
    }

    const handleImageAnnotationChangeClick = event => {
        setImageUrl(null);
        setImageWidth(null);
        setImageHeight(null);
    }

    const handleDataTypeChange = event => {
        const value = event.target.value;

        switch (value) {
            case 'String':
                setIsDefaultNegative(null);
                setDecimalPlaces(null);
                setFormat(null);
                setValidation(null);
                break;

            case 'Integer':
                setIsDefaultNegative(false);
                setDecimalPlaces(null);
                setFormat(null);
                setValidation(null);
                clearPadding();
                break;

            case 'Decimal':
                setIsDefaultNegative(false);
                setDecimalPlaces(2);
                setFormat(null);
                setValidation(null);
                clearPadding();
                break;

            default:
                setIsDefaultNegative(null);
                setDecimalPlaces(null);
                setFormat(null);
                setValidation(null);
                clearPadding();
                break;
        }

        setDataType(value);
        examInput.hasUnsavedChanges = true;
    }

    const handlePaddingTypeChange = event => {
        const value = event.target.value;
        setPaddingType(value);
        examInput.hasUnsavedChanges = true;

        if (!value) {
            setPaddingCharacter(null);
            setPaddingLength(null);
        }
    }

    const handlePaddingCharacterChange = event => {
        const value = event.target.value;
        setPaddingCharacter(value);
        examInput.hasUnsavedChanges = true;

        if (!paddingLength) {
            if (value && value.length > 0) {
                setPaddingLength(2)
            }
        }
    }

    const handlePaddingBlur = event => {
        if (paddingCharacter && !paddingLength) {
            setPaddingLength(2)
        }
        if (!paddingCharacter && paddingLength) {
            setPaddingLength(null)
        }
    }

    const handlePaddingLengthChange = event => {
        const value = parseInt(event.target.value, 10);
        setPaddingLength(value);
        examInput.hasUnsavedChanges = true;
    }

    const handleDefaultNegativeChange = event => {
        const value = !isDefaultNegative;
        setIsDefaultNegative(value);
        examInput.hasUnsavedChanges = true;
    }

    const handleDecimalPlacesChange = event => {
        const value = parseInt(event.target.value, 10);
        setDecimalPlaces(value);
        examInput.hasUnsavedChanges = true;
    }

    const handleFormatChange = event => {
        const value = event.target.value;
        setFormat(value);
        examInput.hasUnsavedChanges = true;
    }

    const handleFormatSearchChange = event => {
        const value = event.target.value;
        setFormatSearch(value);
        examInput.hasUnsavedChanges = true;
    }

    const handleFormatReplaceChange = event => {
        const value = event.target.value;
        setFormatReplace(value);
        examInput.hasUnsavedChanges = true;
    }

    const handleValidationChange = event => {
        const value = event.target.value;
        setValidation(value);
        examInput.hasUnsavedChanges = true;
    }

    const handleStepChange = event => {
        const value = event.target.value;
        const number = !fn.isNullOrUndefined(value) ? Number(value) : null;
        const isDecimal = !isNaN(number) && !Number.isInteger(number);
        const isInt = !isNaN(number) && Number.isInteger(number);

        if (isDecimal || isInt) {
            setStep(value);
        }
        else {
            setStep(null);
        }

        examInput.hasUnsavedChanges = true;
    }

    const handleHistoryTooltipEnabledChange = event => {
        const value = !historyTooltipEnabled;
        setHistoryTooltipEnabled(value);
        examInput.hasUnsavedChanges = true;
    }

    const handleHistoryTooltipExpressionChange = (e) => {
        const html = e.value;
        const expression = (html === '<p></p>') || (html === '<p><br></p>') ? '' : html;
        expressionRef.current = expression;
        examInput.hasUnsavedChanges = true;
    }

    const handleHistoryTooltipExpressionBlur = (e) => {
        expressionRef.current = expressionRef.current.replace('\t', '&nbsp;&nbsp;&nbsp;');
        setHistoryTooltipExpression(expressionRef.current);
    }

    const handleDefaultValueTextChange = event => {
        setDefaultValue(event.target.value);
        examInput.hasUnsavedChanges = true;
    }

    const handleDefaultValueRichTextChange = content => {
        const html = content;
        setDefaultValue(((html === '<p><br></p>') ? null : html));
        examInput.hasUnsavedChanges = true;
    }

    const handleDefaultValueCheckboxChange = event => {
        if (!defaultValue) {
            setDefaultValue([]);
        }

        if (event.target.checked) {
            defaultValue.push(event.target.value);
        } else {
            const index = defaultValue.findIndex(v => v === event.target.value);
            if (index >= 0) {
                defaultValue.splice(index, 1);
            }
        }

        if (defaultValue.length === 0) {
            setDefaultValue(null);
        }

        setDefaultValue(defaultValue);
        examInput.hasUnsavedChanges = true;
    }

    const handleFileTextChange = event => {
        const value = event.target.value;
        setFileText(value);
        examInput.hasUnsavedChanges = true;
    }

    const handleFilePreviewChange = event => {
        const value = event.target.value === 'true';

        if (value) {
            setFileMultiple(false);
        }

        setFilePreview(value);
        examInput.hasUnsavedChanges = true;
    }

    const handleFileMultipleChange = event => {
        const value = event.target.value === 'true';

        if (value) {
            setFilePreview(false);
        }

        setFileMultiple(value);
        examInput.hasUnsavedChanges = true;
    }

    const clearPadding = () => {
        setPaddingType(null);
        setPaddingCharacter(null);
        setPaddingLength(null);
    }

    const renderOptions = () => {
        return <>
            {
                options && options.length > 0 ?
                    options.map((o, oi) => {
                        return <ul
                            key={`exam_input_options_${oi}`}
                            className='list-inline no-style m-0 mb-2'
                        >
                            <li className='list-inline-item m-0 mr-1'>{oi + 1}.&nbsp;</li>
                            <li className='list-inline-item m-0 option'>
                                <input
                                    id={`new-exam-input-${examInput.data.key}-${oi}`}
                                    type='text'
                                    className='form-control form-control-sm'
                                    autoComplete='off'
                                    value={o}
                                    onChange={(e) => handleOptionChange(e, oi)}
                                />
                            </li>
                            <li className='list-inline-item m-0 ml-2'>
                                <button
                                    type='button'
                                    className='btn btn-link btn-icon btn-sm'
                                    autoComplete='off'
                                    onClick={e => { handleRemoveOption(o, oi) }}
                                >
                                    <i className={'fal fa-minus-circle text-danger'}></i>
                                </button>
                            </li>
                            <li className='list-inline-item m-0'>
                                <button
                                    type='button'
                                    className='btn btn-link btn-icon btn-sm'
                                    autoComplete='off'
                                    disabled={options.length === 1 || oi === 0}
                                    onClick={e => { handleMoveOption(oi, (oi - 1)) }}
                                >
                                    <i className={'fal fa-arrow-up' + (options.length === 1 || oi === 0 ? ' text-gray-500' : ' text-primary')}></i>
                                </button>
                            </li>
                            <li className='list-inline-item m-0'>
                                <button
                                    type='button'
                                    className='btn btn-link btn-icon btn-sm'
                                    disabled={options.length === 1 || oi === (options.length - 1)}
                                    onClick={e => { handleMoveOption(oi, (oi + 1)) }}
                                >
                                    <i className={'fal fa-arrow-down' + (options.length === 1 || oi === (options.length - 1) ? ' text-gray-500' : ' text-primary')}></i>
                                </button>
                            </li>
                        </ul>
                    }) : <div></div>
            }
            <button
                type='button'
                className='btn btn-link btn-icon-left btn-sm'
                onClick={handleAddOption}
            >
                <i className='fal fa-plus-circle'></i> Add option
            </button>
        </>
    }

    const renderDefaultValueEditor = () => {
        switch (examInput.data.type) {
            case 'SingleLineText':
                return <input
                    id='new-exam-input-default'
                    type='text'
                    maxLength={100}
                    className='form-control'
                    value={defaultValue ? defaultValue : ''}
                    autoComplete={'off'}
                    onChange={handleDefaultValueTextChange}
                />;

            case 'MultiLineText':
                return <textarea
                    id='new-exam-input-default'
                    className='form-control'
                    value={defaultValue ? defaultValue : ''}
                    autoComplete={'off'}
                    onChange={handleDefaultValueTextChange}
                />;
            // return <RichTextEditor
            //     id='new-exam-input-default'
            //     mode='none'
            //     disableTab={true}
            //     value={defaultValue ? defaultValue : ''}
            //     onChange={handleDefaultValueRichTextChange}
            // />;

            case 'RichText':
                return <RichTextEditor
                    id='new-exam-input-default'
                    mode='full'
                    disableTab={true}
                    value={defaultValue ? defaultValue : ''}
                    onChange={handleDefaultValueRichTextChange}
                />;

            case 'Dropdown':
                return <select
                    id='new-exam-input-default'
                    className='custom-select form-control'
                    value={defaultValue}
                    onChange={handleDefaultValueTextChange}
                >
                    <option value=''></option>
                    {
                        options && options.length > 0 ?
                            options.map((o, oi) => {
                                return <option key={`${examInput.key}_metadata_${oi}`} value={o}>{o}</option>
                            }) : null
                    }
                </select>;

            case 'Radio':
                return <div className='radio-wrapper'>
                    {
                        options && options.length > 0 ?
                            options.map((o, oi) => {
                                return <div
                                    key={`${examInput.key}_metadata_${oi}`}
                                    className={'custom-control custom-radio mb-2'}
                                >
                                    <input
                                        id={`new-exam-input-default-${examInput.key}-${oi}`}
                                        type='radio'
                                        name='exam-input-default'
                                        className='custom-control-input'
                                        value={o}
                                        checked={defaultValue === o}
                                        onChange={handleDefaultValueTextChange}
                                    />
                                    <label
                                        htmlFor={`new-exam-input-inline-${examInput.key}-${oi}`}
                                        className='custom-control-label'
                                    >
                                        {o}
                                    </label>
                                </div>
                            }) : null
                    }
                </div>;

            case 'Checkbox':
                return <div className='checkbox-wrapper'>
                    {
                        options && options.length > 0 ?
                            options.map((o, oi) => {
                                return <div
                                    key={`${examInput.key}_metadata_${oi}`}
                                    className={'custom-control custom-checkbox mb-2'}
                                >
                                    <input
                                        id={`new-exam-input-default-${examInput.key}-${oi}`}
                                        type='checkbox'
                                        name='exam-input-default'
                                        className='custom-control-input'
                                        value={o}
                                        checked={defaultValue && defaultValue.split('|').some(v => v === o)}
                                        onChange={handleDefaultValueCheckboxChange}
                                    />
                                    <label
                                        htmlFor={`new-exam-input-inline-${examInput.key}-${oi}`}
                                        className='custom-control-label'
                                    >
                                        {o}
                                    </label>
                                </div>
                            }) : null
                    }
                </div>;

            case 'Date':
            case 'Time':
            default:
                return null
        }
    }

    const populateMetadata = (field, key, value) => {
        if (tih.hasField(examInput.data.type, field)) {
            examInput.setMetadata(key, value);
        } else {
            examInput.removeMetadata(key);
        }
    }

    const setFocusToName = () => {
        focusTimer.current = setTimeout(() => {
            const element = document.querySelector('#new-exam-input-name');
            if (element) {
                element.focus();
            }
        }, 100)
    }

    return useObserver(() => <>
        <>
            {
                (props.drawer === quickDrawer.drawerOpened) ?
                    <GlobalHotKeys
                        keyMap={{
                            close: ['esc'],
                        }}
                        handlers={{
                            close: event => {
                                handleCancel(event)
                            },
                        }}
                        allowChanges={true}
                    /> : null
            }
        </>
        <form onSubmit={handleSubmit}>
            <fieldset disabled={examInput.isSaving}>
                <div className='quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('exam-input', 'default')}
                        action='New'
                        category='Exam Input'
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        <FadeIn>
                            <div className='new-exam-input body-content'>
                                <section>
                                    <div className='row'>
                                        <div className='col-12'>
                                            <div className='form-group mb-4'>
                                                <label className='required' htmlFor='new-exam-input-type'><small>Type</small></label>
                                                <select
                                                    id='new-exam-input-type'
                                                    className='custom-select form-control'
                                                    value={examInput.data.type ? examInput.data.type : ''}
                                                    onChange={handleTypeChange}
                                                >
                                                    <option value=''></option>
                                                    {
                                                        cache.getReferenceDataOptions('TemplateInputType').map((option, di) => {
                                                            return <option key={`exam_input_type_${di}`} value={option.key}>{option.value}</option>
                                                        })
                                                    }
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    {
                                        metadataReady ?
                                            <FadeIn>
                                                <>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='form-group mb-4'>
                                                                <label className='required' htmlFor='new-exam-input-name'><small>Name</small></label>
                                                                <input
                                                                    id='new-exam-input-name'
                                                                    type='text'
                                                                    maxLength={100}
                                                                    className='form-control'
                                                                    autoComplete={'off'}
                                                                    value={examInput.data.name ? examInput.data.name : ''}
                                                                    onChange={handleNameChange}
                                                                />
                                                                <small className='text-gray-700'><strong>Important:</strong> Name must be unique across all inputs.</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_LABEL) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label htmlFor='new-exam-input-label'><small>Label</small></label>
                                                                        <input
                                                                            id='new-exam-input-label'
                                                                            type='text'
                                                                            maxLength={100}
                                                                            className='form-control'
                                                                            value={examInput.data.label ? examInput.data.label : ''}
                                                                            autoComplete={'off'}
                                                                            onChange={handleLabelChange}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_SIZE) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label className='required' htmlFor='new-exam-input-size'><small>Size</small></label>
                                                                        <select
                                                                            id='new-exam-input-size'
                                                                            className='custom-select form-control'
                                                                            value={size ? size : ''}
                                                                            onChange={handleSizeChange}
                                                                        >
                                                                            <option value='sm'>Small</option>
                                                                            <option value=''>Standard</option>
                                                                            <option value='lg'>Large</option>
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_ALIGN) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label className='required' htmlFor='new-exam-input-align'><small>Align</small></label>
                                                                        <div>
                                                                            <div className='btn-group'>
                                                                                <button
                                                                                    type='button'
                                                                                    className={'btn btn-secondary' + (align === 'left' ? ' active' : '')}
                                                                                    value={(align ? align : '')}
                                                                                    onClick={() => { handleAlignChange('left') }}
                                                                                >
                                                                                    <i className='fal fa-align-left'></i>
                                                                                </button>
                                                                                <button
                                                                                    type='button'
                                                                                    className={'btn btn-secondary' + (align === 'center' ? ' active' : '')}
                                                                                    value={(align ? align : '')}
                                                                                    onClick={() => { handleAlignChange('center') }}
                                                                                >
                                                                                    <i className='fal fa-align-center'></i>
                                                                                </button>
                                                                                <button
                                                                                    type='button'
                                                                                    className={'btn btn-secondary' + (align === 'right' ? ' active' : '')}
                                                                                    value={(align ? align : '')}
                                                                                    onClick={() => { handleAlignChange('right') }}
                                                                                >
                                                                                    <i className='fal fa-align-right'></i>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_VALIGN) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label className='required' htmlFor='new-exam-input-valign'><small>Vertical Align</small></label>
                                                                        <div>
                                                                            <div className='btn-group'>
                                                                                <button
                                                                                    type='button'
                                                                                    className={'btn btn-secondary' + (valign === 'top' ? ' active' : '')}
                                                                                    value={(valign ? valign : '')}
                                                                                    onClick={() => { handleValignChange('top') }}
                                                                                >
                                                                                    <i className='fal fa-arrow-to-top'></i>
                                                                                </button>
                                                                                <button
                                                                                    type='button'
                                                                                    className={'btn btn-secondary' + (valign === 'middle' ? ' active' : '')}
                                                                                    value={(valign ? valign : '')}
                                                                                    onClick={() => { handleValignChange('middle') }}
                                                                                >
                                                                                    <i className='fal fa-horizontal-rule'></i>
                                                                                </button>
                                                                                <button
                                                                                    type='button'
                                                                                    className={'btn btn-secondary' + (valign === 'bottom' ? ' active' : '')}
                                                                                    value={(valign ? valign : '')}
                                                                                    onClick={() => { handleValignChange('bottom') }}
                                                                                >
                                                                                    <i className='fal fa-arrow-to-bottom'></i>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_INLINE) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label><small>Inline</small></label>
                                                                        <div>
                                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                                <input
                                                                                    id={`new-exam-input-inline-true`}
                                                                                    type='radio'
                                                                                    name='exam-input-inline'
                                                                                    className='custom-control-input'
                                                                                    value={true}
                                                                                    checked={inline}
                                                                                    onChange={handleInlineChange}
                                                                                />
                                                                                <label
                                                                                    htmlFor={`new-exam-input-inline-true`}
                                                                                    className='custom-control-label'
                                                                                >
                                                                                    Yes
                                                                                </label>
                                                                            </div>
                                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                                <input
                                                                                    id={`new-exam-input-inline-false`}
                                                                                    type='radio'
                                                                                    name='exam-input-inline'
                                                                                    className='custom-control-input'
                                                                                    value={false}
                                                                                    checked={!inline}
                                                                                    onChange={handleInlineChange}
                                                                                />
                                                                                <label
                                                                                    htmlFor={`new-exam-input-inline-false`}
                                                                                    className='custom-control-label'
                                                                                >
                                                                                    No
                                                                                </label>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_IMAGE_URL) ?
                                                            <div className='row options'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label className='required' htmlFor='new-exam-input-options'><small>Image</small></label>
                                                                        {
                                                                            imageUrl ?
                                                                                <div>
                                                                                    <img src={imageUrl} width={imageWidth} height={imageHeight} alt='Annotate' />
                                                                                    <button
                                                                                        type='button'
                                                                                        className='btn btn-link px-0 btn-sm d-block'
                                                                                        onClick={handleImageAnnotationChangeClick}
                                                                                    >Change image</button>
                                                                                </div> :
                                                                                <div className='custom-file'>
                                                                                    <input
                                                                                        id='new-exam-input-image-url'
                                                                                        type='file'
                                                                                        className='custom-file-input'
                                                                                        accept='image/*'
                                                                                        onChange={handleImageAnnotationChange} />
                                                                                    <label
                                                                                        htmlFor='new-exam-input-image-url'
                                                                                        className='custom-file-label'>
                                                                                    </label>
                                                                                </div>
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_OPTIONS) ?
                                                            <div className='row options'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label className='required' htmlFor='new-exam-input-options'><small>Options</small></label>
                                                                        {renderOptions()}
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_FILE_TEXT) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label htmlFor='new-exam-input-file-text'><small>Upload text</small></label>
                                                                        <input
                                                                            id='new-exam-input-file-text'
                                                                            type='text'
                                                                            maxLength={100}
                                                                            className='form-control'
                                                                            value={fileText ? fileText : ''}
                                                                            autoComplete={'off'}
                                                                            onChange={handleFileTextChange}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_FILE_PREVIEW) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label><small>Display file content</small></label>
                                                                        <div>
                                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                                <input
                                                                                    id={`new-exam-input-file-preview-true`}
                                                                                    type='radio'
                                                                                    name='exam-input-file-preview'
                                                                                    className='custom-control-input'
                                                                                    value={true}
                                                                                    checked={filePreview}
                                                                                    onChange={handleFilePreviewChange}
                                                                                />
                                                                                <label
                                                                                    htmlFor={`new-exam-input-file-preview-true`}
                                                                                    className='custom-control-label'
                                                                                >
                                                                                    Yes
                                                                                </label>
                                                                            </div>
                                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                                <input
                                                                                    id={`new-exam-input-file-preview-false`}
                                                                                    type='radio'
                                                                                    name='exam-input-file-preview'
                                                                                    className='custom-control-input'
                                                                                    value={false}
                                                                                    checked={!filePreview}
                                                                                    onChange={handleFilePreviewChange}
                                                                                />
                                                                                <label
                                                                                    htmlFor={`new-exam-input-file-preview-false`}
                                                                                    className='custom-control-label'
                                                                                >
                                                                                    No
                                                                                </label>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_FILE_MULTIPLE) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label><small>Allow multiple files</small></label>
                                                                        <div>
                                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                                <input
                                                                                    id={`new-exam-input-file-multiple-true`}
                                                                                    type='radio'
                                                                                    name='exam-input-file-multiple'
                                                                                    className='custom-control-input'
                                                                                    value={true}
                                                                                    checked={fileMultiple}
                                                                                    onChange={handleFileMultipleChange}
                                                                                />
                                                                                <label
                                                                                    htmlFor={`new-exam-input-file-multiple-true`}
                                                                                    className='custom-control-label'
                                                                                >
                                                                                    Yes
                                                                                </label>
                                                                            </div>
                                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                                <input
                                                                                    id={`new-exam-input-file-multiple-false`}
                                                                                    type='radio'
                                                                                    name='exam-input-file-multiple'
                                                                                    className='custom-control-input'
                                                                                    value={false}
                                                                                    checked={!fileMultiple}
                                                                                    onChange={handleFileMultipleChange}
                                                                                />
                                                                                <label
                                                                                    htmlFor={`new-exam-input-file-multiple-false`}
                                                                                    className='custom-control-label'
                                                                                >
                                                                                    No
                                                                                </label>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_TYPE) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label htmlFor='new-exam-input-data-type'><small>Data Type</small></label>
                                                                        <select
                                                                            id='new-exam-input-data-type'
                                                                            className='custom-select form-control'
                                                                            value={dataType ? dataType : ''}
                                                                            onChange={handleDataTypeChange}
                                                                        >
                                                                            <option value=''>(Not set)</option>
                                                                            {
                                                                                tih.getDataTypes().map((dt, dti) => {
                                                                                    return <option
                                                                                        key={`new-exam-input-data-type_${dti}`}
                                                                                        value={dt}
                                                                                    >{dt}</option>
                                                                                })
                                                                            }
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_TYPE) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_PADDING_TYPE) ?
                                                            <div className='row'>
                                                                <div className='col-4'>
                                                                    <div className='form-group mb-4'>
                                                                        <label htmlFor='new-exam-input-padding-type'><small>Data Padding</small></label>
                                                                        <select
                                                                            id='new-exam-input-padding-type'
                                                                            className='custom-select form-control'
                                                                            value={paddingType ? paddingType : ''}
                                                                            onChange={handlePaddingTypeChange}
                                                                        >
                                                                            <option value=''>(None)</option>
                                                                            <option value='left'>Left</option>
                                                                            <option value='right'>Right</option>
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                                <div className='col-4'>
                                                                    <div className='form-group mb-4'>
                                                                        <label htmlFor='new-exam-input-padding-char'><small>Character</small></label>
                                                                        <input
                                                                            id='new-exam-input-padding-char'
                                                                            type='text'
                                                                            maxLength={1}
                                                                            className='form-control'
                                                                            autoComplete='off'
                                                                            value={paddingCharacter ? paddingCharacter : ''}
                                                                            disabled={!paddingType}
                                                                            onChange={handlePaddingCharacterChange}
                                                                            onBlur={handlePaddingBlur}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className='col-4'>
                                                                    <div className='form-group mb-4'>
                                                                        <label htmlFor='new-exam-input-padding-length'><small>Length</small></label>
                                                                        <input
                                                                            id='new-exam-input-padding-length'
                                                                            type='number'
                                                                            min={2}
                                                                            max={10}
                                                                            step={1}
                                                                            className='form-control'
                                                                            autoComplete='off'
                                                                            disabled={!paddingCharacter || paddingCharacter.length === 0}
                                                                            value={paddingLength ? paddingLength : ''}
                                                                            onChange={handlePaddingLengthChange}
                                                                            onBlur={handlePaddingBlur}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_DEFAULT_NEGATIVE) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_DEFAULT_NEGATIVE) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <div className='custom-control custom-switch'>
                                                                            <input
                                                                                id='new-exam-input-default-negative'
                                                                                type='checkbox'
                                                                                name='new-exam-input-default-negative'
                                                                                className='custom-control-input'
                                                                                checked={isDefaultNegative}
                                                                                onChange={handleDefaultNegativeChange}
                                                                            />
                                                                            <label
                                                                                htmlFor='new-exam-input-default-negative'
                                                                                className='custom-control-label'
                                                                            >
                                                                                Default as negative number
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_DECIMAL_PLACES) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_DECIMAL_PLACES) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label htmlFor='new-exam-input-decimal-places'><small>Decimal Places</small></label>
                                                                        <input
                                                                            id='new-exam-input-decimal-places'
                                                                            type='number'
                                                                            min={1}
                                                                            max={5}
                                                                            step={1}
                                                                            className='form-control'
                                                                            autoComplete='off'
                                                                            value={decimalPlaces ? decimalPlaces : ''}
                                                                            onChange={handleDecimalPlacesChange}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label htmlFor='new-exam-input-format'><small>Format</small></label>
                                                                        {
                                                                            tih.getFormatOptions(dataType, decimalPlaces).map((o, oi) => {
                                                                                return <div
                                                                                    key={`new-exam-input-format-${oi}`}
                                                                                    className='custom-control custom-radio mb-2'
                                                                                >
                                                                                    <input
                                                                                        id={`new-exam-input-format-${oi}`}
                                                                                        type='radio'
                                                                                        name='updateExamInputFormat'
                                                                                        value={o.type}
                                                                                        className='custom-control-input'
                                                                                        checked={format === o.type}
                                                                                        onChange={handleFormatChange}
                                                                                    />
                                                                                    <label
                                                                                        htmlFor={`new-exam-input-format-${oi}`}
                                                                                        className='custom-control-label'
                                                                                    >
                                                                                        {o.example}
                                                                                    </label>
                                                                                </div>
                                                                            })
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT_SEARCH) && tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_FORMAT_REPLACE) ?
                                                            <div className='row'>
                                                                <div className='col-6'>
                                                                    <div className='form-group mb-4'>
                                                                        <label htmlFor='new-exam-input-format-search'><small>Search <span className='tt-normal fw-400'>(Regular Expression)</span></small></label>
                                                                        <select
                                                                            id='new-exam-input-format-search'
                                                                            className='form-control'
                                                                            value={formatSearch ? formatSearch : ''}
                                                                            onChange={handleFormatSearchChange}
                                                                        >
                                                                            <option></option>
                                                                            <option value={JSON.stringify([
                                                                                "^([+-]?)(\\d*)(\\d{2})\\.00$",
                                                                                "^([+-]?)\\."
                                                                            ])}>Rx without Decimal</option>
                                                                            <option value={'^0+$'}>Axis ZERO</option>
                                                                            <option value={'^[+-]?0(\\.0+)?$'}>Number ZERO</option>
                                                                            <option value={'^(\\d+)([+-]\\d*)?$'}>Visual Acuity number</option>
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                                <div className='col-6'>
                                                                    <div className='form-group mb-4'>
                                                                        <label htmlFor='new-exam-input-format-replace'><small>Replace <span className='tt-normal fw-400'>(Regular Expression)</span></small></label>
                                                                        <select
                                                                            id='new-exam-input-format-replace'
                                                                            className='form-control'
                                                                            value={formatReplace ? formatReplace : ''}
                                                                            onChange={handleFormatReplaceChange}
                                                                        >
                                                                            <option></option>
                                                                            <option value={'["$1$2.$3","$10."]'}>Auto Decimal</option>
                                                                            <option value={'Plano'}>Plano</option>
                                                                            <option value={'PL'}>PL</option>
                                                                            <option value={'SPH'}>SPH</option>
                                                                            <option value={'180'}>Axis (0 = 180)</option>
                                                                            <option value={'20/$1$2'}>Visual Acuity</option>
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_VALIDATION) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label htmlFor='new-exam-input-validation-regex'><small>Validation <span className='tt-normal fw-400'>(Regular Expression)</span></small></label>
                                                                        <input
                                                                            id='new-exam-input-validation-regex'
                                                                            type='text'
                                                                            maxLength={255}
                                                                            className='form-control'
                                                                            autoComplete='off'
                                                                            value={validation ? validation : ''}
                                                                            onChange={handleValidationChange}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_DATA_STEP) && tih.checkDataTypeOption(dataType, tih.TEMPLATE_INPUT_METADATA_DATA_STEP) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label htmlFor='new-exam-input-step'><small>Step</small></label>
                                                                        <input
                                                                            id='new-exam-input-step'
                                                                            type='text'
                                                                            maxLength={255}
                                                                            className='form-control'
                                                                            autoComplete='off'
                                                                            value={step ? step : ''}
                                                                            onChange={handleStepChange}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_HISTORY_TOOLTIP) ?
                                                            <>
                                                                <div className='row'>
                                                                    <div className='col-12'>
                                                                        <div className='form-group'>
                                                                            <label htmlFor='new-exam-input-history-tooltip'><small>History Tooltip</small></label>
                                                                            <div className='custom-control custom-checkbox mb-2'>
                                                                                <input
                                                                                    id='new-exam-input-history-tooltip'
                                                                                    type='checkbox'
                                                                                    name='new-exam-input-history-tooltip'
                                                                                    className='custom-control-input'
                                                                                    checked={historyTooltipEnabled}
                                                                                    onChange={handleHistoryTooltipEnabledChange}
                                                                                />
                                                                                <label
                                                                                    htmlFor='new-exam-input-history-tooltip'
                                                                                    className='custom-control-label'
                                                                                >
                                                                                    Enable History Tooltip
                                                                                </label>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className='row'>
                                                                    <div className='col-12'>
                                                                        <div className='form-group mb-4'>
                                                                            <div>
                                                                                <HtmlEditor
                                                                                    placeholder='Leave as [blank] to show history of this field by default'
                                                                                    disabled={!historyTooltipEnabled}
                                                                                    disableTab={false}
                                                                                    defaultValue={historyTooltipExpression}
                                                                                    onValueChanged={handleHistoryTooltipExpressionChange}
                                                                                    onFocusOut={handleHistoryTooltipExpressionBlur}
                                                                                >
                                                                                    <Mention
                                                                                        marker={'#'}
                                                                                        minSearchLength={2}
                                                                                        dataSource={(props && props.extraProps && props.extraProps.examDataKeys ? props.extraProps.examDataKeys : [])}
                                                                                        displayExpr={'id'}
                                                                                        valueExpr={'id'}
                                                                                        searchExpr={['id', 'display']}
                                                                                    />
                                                                                </HtmlEditor>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_METADATA_DEFAULT_VALUE) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label htmlFor='new-exam-input-default'><small>Default Value</small></label>
                                                                        {renderDefaultValueEditor()}
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    {
                                                        tih.hasField(examInput.data.type, tih.TEMPLATE_INPUT_DESCRIPTION) ?
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label htmlFor='new-exam-input-description'><small>Description</small></label>
                                                                        <RichTextEditor
                                                                            mode='none'
                                                                            disableTab={true}
                                                                            value={examInput.data.descriptionHtml ? examInput.data.descriptionHtml : ''}
                                                                            onChange={handleDescriptionChange}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div> : null
                                                    }
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='form-group mb-4'>
                                                                <label className='required' htmlFor='new-exam-input-key'><small>Key</small></label>
                                                                <input
                                                                    id='new-exam-input-key'
                                                                    type='text'
                                                                    maxLength={100}
                                                                    className='form-control'
                                                                    autoComplete='off'
                                                                    value={examInput.data.key ? examInput.data.key : ''}
                                                                    onChange={handleKeyChange}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            </FadeIn> : null
                                    }
                                </section>
                            </div>
                        </FadeIn>
                    </div>
                    <div className='quick-drawer-action'>
                        <div className='row'>
                            <div className='col-4'>
                                <button
                                    type='button'
                                    className='btn btn-link px-0'
                                    onClick={handlePreviewClick}
                                >Preview</button>
                            </div>
                            <div className='col-8'>
                                <div className='float-right'>
                                    <button
                                        type='button'
                                        className='btn btn-link btn-cancel mr-2'
                                        onClick={handleCancel}
                                    >Cancel</button>
                                    <button
                                        type='submit'
                                        className='btn btn-success'
                                    >Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
        <BodyEnd>
            <div className={'modal fade d-block' + (displayPreview ? ' show' : '')} tabIndex='-1' role='dialog' aria-modal='true'>
                <div className='modal-dialog modal-dialog-centered'>
                    <div className='modal-content'>
                        <div className='modal-header'>
                            <h5 className='modal-title'>
                                Quick Preview
                                <small className='m-0 text-gray-700'>Below is the preview of your <code>{examInput.data.type}</code> input.</small>
                            </h5>
                            <button
                                type='button'
                                className='close'
                                data-dismiss='modal'
                                aria-label='Close'
                                onClick={handleClosePreviewClick}
                            >
                                <span aria-hidden='true'><i className='fal fa-times'></i></span>
                            </button>
                        </div>
                        <div className='modal-body pb-0'>
                            {tih.renderInput(examInput.data, { isDesignMode: true })}
                        </div>
                        <div className='modal-footer pt-0'>
                            <button
                                type='button'
                                className='btn btn-sm btn-primary'
                                onClick={handleClosePreviewClick}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className={'modal-backdrop fade' + (displayPreview ? ' show' : '')} onClick={handleClosePreviewClick}></div>
        </BodyEnd>
    </>)
}

export default NewExamInput;